import {
    WarehouseStock,
    PosItemModel,
    SkuPLURateModel,
    StockDocHeadersWithItems,
    StockDocItemModel,
    StockKeepingUnitModel,
    WarehouseModel,
    EntryCalcHeadersWithItems,
    EntryCalculationItemModel,
    StockDocHeaderModel,
    EntryCalculationHeaderModel,
    SkuPluRateWithItem,
    InventoryWithExistingAndOtherItems,
    SupplierPriceListsExtended,
    SupplierPriceListToSheet,
    SkuSupplierPriceListPerHeaderModel,
    SkuSupplierPriceListPerHeaderEditModel,
    SkuItemSupplierCodeWithCompanyAndSkuItem,
    StockDocHeaderEditModel,
    PosTerminalEditModel,
    StockKeepingUnitEditModel,
    PriceChangeLogsHeadersExtended,
    PriceChangeLogHeaderWithItems,
    PriceChangeLogHeaderModel,
    PriceChangeLogItemModel,
    PaginatedData,
    InitialBalanceModel,
    PriceChangeLogHeaderTypeEnum,
    EntryCalculationHeaderEditModel,
} from '@common/modelDefinition';
import rest from '@data/restWrapper';
import useStore from '@data/state/zustand';
import translate from '@data/translations';

export const getSKUItems = async (
    page: number,
    rows: number,
    onlyActive?: boolean,
    filterType?: number | null,
    getSemiFinalItems?: boolean
): Promise<PaginatedData> => {
    return onlyActive
        ? getSemiFinalItems === true || getSemiFinalItems === false
            ? await rest(
                  '/getStockKeepingUnits?page=' +
                      page +
                      '&rows=' +
                      rows +
                      '&onlyActive=' +
                      onlyActive +
                      '&filterType=' +
                      filterType +
                      '&getSemiFinalItems=' +
                      getSemiFinalItems
              )
            : await rest(
                  '/getStockKeepingUnits?page=' +
                      page +
                      '&rows=' +
                      rows +
                      '&onlyActive=' +
                      onlyActive +
                      '&filterType=' +
                      filterType
              )
        : await rest('/getStockKeepingUnits?page=' + page + '&rows=' + rows + '&filterType=' + filterType);
};

export const createOrEditSkuItemAndSkuPluRate = async (
    skuItem: StockKeepingUnitModel | null,
    skuPluRates: SkuPLURateModel[],
    skuItems?: StockKeepingUnitModel[]
): Promise<StockKeepingUnitModel[]> => {
    return await rest('/createOrEditSkuItemAndSkuPluRate', 'POST', { skuItem, skuPluRates, skuItems });
};

export const deleteSKUItem = async (item: StockKeepingUnitModel): Promise<StockKeepingUnitModel[]> => {
    let skuItem = { ...item };
    skuItem.active = false;
    return await rest('/createOrEditSkuItemAndSkuPluRate', 'POST', { skuItem });
};

export const checkIfBarcodeOrCodeIsUnique = async (
    barcode: string | null,
    code: string | null,
    skuItemId?: number
): Promise<number> => {
    return await rest(
        '/checkIfBarcodeOrCodeIsUnique/' + (skuItemId ? skuItemId : null) + '/' + barcode + '/' + code,
        'GET'
    );
};

export const checkIfStockDocHeaderDocNumberExist = async (
    docHeaderId: number | null,
    docNumber: string,
    docType: number,
    conclusionType?: number
): Promise<boolean> => {
    return await rest(
        '/checkIfStockDocHeaderDocNumberExist/' +
            docHeaderId +
            '/' +
            docNumber +
            '/' +
            docType +
            '?conclusionType=' +
            (Number(conclusionType) ? Number(conclusionType) : 0),
        'GET'
    );
};

export const getDocumentOrEntryCalcNumSuggestion = async (
    docType: string | null,
    document?: boolean,
    conclusionType?: number,
    year?: number
): Promise<{docNumber: string, sequenceNumber: number}> => {
    if (document) {
        return await rest(
            '/getDocumentOrEntryCalcNumSuggestion/' +
                docType +
                '?document=' +
                document +
                '&conclusionType=' +
                (Number(conclusionType) ? Number(conclusionType) : 0) +
                '&selectedYear=' +
                year,
            'GET'
        );
    } else {
        return await rest('/getDocumentOrEntryCalcNumSuggestion/' + docType + '?selectedYear=' + year, 'GET');
    }
};

export const getLockedOrUnLockedStockDocHeaders = async (
    getLockedHeaders?: boolean,
    page?: number,
    rows?: number
): Promise<PaginatedData> => {
    if (getLockedHeaders) {
        return await rest(
            '/getLockedOrUnLockedStockDocHeaders?getLockedHeaders=' +
                getLockedHeaders +
                '&page=' +
                page +
                '&rows=' +
                rows,
            'GET'
        );
    } else {
        return await rest('/getLockedOrUnLockedStockDocHeaders', 'GET');
    }
};

export const getLockedOrUnLockedInititalBalances = async (
    getLockedHeaders?: boolean,
    page?: number,
    rows?: number
): Promise<PaginatedData> => {
    if (getLockedHeaders) {
        return await rest(
            '/getLockedOrUnLockedInititalBalances?getLockedHeaders=' +
                getLockedHeaders +
                '&page=' +
                page +
                '&rows=' +
                rows,
            'GET'
        );
    } else {
        return await rest('/getLockedOrUnLockedInititalBalances', 'GET');
    }
};

export const loadGoodsReceivedNotes = async (): Promise<StockDocHeaderEditModel[]> => {
    return await rest('/loadGoodsReceivedNotes');
};

export const getStockDocHeaderWithItems = async (docHeaderId: number): Promise<StockDocHeadersWithItems> => {
    return await rest('/getStockDocHeaderWithItems/' + docHeaderId, 'GET');
};

export const stornoStockDocument = async (stockDocHeaderId: number): Promise<StockDocHeadersWithItems> => {
    return await rest('/stornoStockDocument/' + stockDocHeaderId, 'POST');
};

export const stornoPriceChangeLog = async (
    priceChangeLogHeaderId: number
): Promise<PriceChangeLogHeaderWithItems | void> => {
    return await rest('/stornoPriceChangeLog/' + priceChangeLogHeaderId, 'POST');
};

export const saveEditedStockDocument = async (
    stockDocument: StockDocHeadersWithItems | StockDocHeaderModel,
    stockDocItems: StockDocItemModel[]
): Promise<StockDocHeadersWithItems> => {
    return await rest('/saveEditedStockDocument', 'POST', {
        stockDocument,
        stockDocItems,
    });
};

export const createStockDocument = async (stockDocument: StockDocHeaderModel): Promise<StockDocHeadersWithItems> => {
    return await rest('/saveEditedStockDocument', 'POST', {
        stockDocument,
        stockDocItems: [],
    });
};

export const searchSkuItem = async (
    searchString: string | number,
    onlyActive?: boolean
): Promise<StockKeepingUnitEditModel[]> => {
    return await rest(`/searchSkuItem?search=${searchString}&onlyActive=${onlyActive}`);
};

export const searchSkuItemNormative = async (searchString: string | number): Promise<SkuPluRateWithItem[]> => {
    return await rest(`/searchSkuItemNormative?search=${searchString}`);
};

export const searchPosItemNormative = async (searchString: string | number): Promise<SkuPluRateWithItem[]> => {
    return await rest(`/searchPosItemNormative?search=${searchString}`);
};

export const getStockCardData = async (
    skuItemId: number,
    from: number,
    to: number,
    page: number,
    rows: number,
    warehouseId?: number | null
): Promise<PaginatedData> => {
    const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : '';
    return await rest(`/getStockCardData/${skuItemId}/${from}/${to}?page=${page}&rows=${rows}${warehouseParam}`);
};

export const getSkuPluRate = async (
    skuItemId: number | null,
    posItemId: number | null,
    semiFinalItemRates?: boolean
): Promise<SkuPluRateWithItem[]> => {
    return semiFinalItemRates
        ? await rest('/getSkuPluRate', 'POST', { skuItemId: skuItemId, posItemId: posItemId, semiFinalItemRates })
        : await rest('/getSkuPluRate', 'POST', { skuItemId: skuItemId, posItemId: posItemId });
};

export const createOrUpdatePosItemAndSkuPluRate = async (
    posItem: PosItemModel | null,
    skuPluRates: SkuPLURateModel[],
    posItems?: PosItemModel[]
): Promise<PosItemModel[]> => {
    return await rest('/createOrUpdatePosItemAndSkuPluRate', 'POST', { posItem, skuPluRates, posItems });
};

export const createOrEditWarehouse = async (warehouse: WarehouseModel): Promise<WarehouseModel> => {
    const newOrEditedWarehouse = await rest('/createOrEditWarehouse', 'POST', { warehouse });
    const warehousesState = useStore.getState().model.Warehouse;
    const warehouses = warehousesState ? [...warehousesState] : [];
    if (!warehouse.id) {
        warehouses.push(newOrEditedWarehouse);
        useStore.getState().setNestedProperty(['model', 'Warehouse'], [...warehouses]);
    } else {
        const _editedWarehouse = warehouses.map((w: WarehouseModel) => {
            if (w.id === warehouse?.id) {
                return warehouse;
            } else {
                return w;
            }
        });
        useStore.getState().setNestedProperty(['model', 'Warehouse'], [..._editedWarehouse]);
    }
    return newOrEditedWarehouse;
};

export const deleteWarehouse = async (warehouseId: number): Promise<void> => {
    await rest('/deleteWarehouse/' + warehouseId, 'POST');
    const warehouses = useStore.getState().model.Warehouse;
    const _warehouses = warehouses?.filter((w: WarehouseModel) => w.id !== warehouseId);
    useStore.getState().setNestedProperty(['model', 'Warehouse'], _warehouses);
};

export const concludeStockDocument = async (headerId: number): Promise<void> => {
    return await rest('/concludeStockDocument/' + headerId, 'POST');
};

export const concludeStockDocAndCreateCalc = async (headerId: number, selectedItemTypes: number[]): Promise<number> => {
    return await rest('/concludeStockDocAndCreateCalc/' + headerId, 'POST', { selectedItemTypes });
};

export const getCurrentWarehouseStock = async (warehouseId: number): Promise<WarehouseStock[]> => {
    const res = await rest('/getCurrentWarehouseStock/' + warehouseId, 'GET');
    return res;
};

export const getWarehouseStockForDate = async (warehouseId: number, date: number): Promise<WarehouseStock[]> => {
    const res = await rest('/getWarehouseStockForDate/' + warehouseId + '/' + date, 'GET');
    return res;
};

export const saveGeneralSettings = async (obj: any): Promise<any> => {
    return await rest('/saveGeneralSetting', 'POST', obj);
};

export const getPrevOrNextDocumentOrEntryCalc = async (
    type: string,
    selectedId: number,
    document?: boolean
): Promise<number> => {
    if (document) {
        return await rest(
            '/getPrevOrNextDocumentOrEntryCalc?type=' + type + '&selectedId=' + selectedId + '&document=' + document,
            'GET'
        );
    } else {
        return await rest('/getPrevOrNextDocumentOrEntryCalc?type=' + type + '&selectedId=' + selectedId, 'GET');
    }
};

export const getInventoryData = async (
    warehouseId: number,
    headerId: number | null
): Promise<InventoryWithExistingAndOtherItems> => {
    console.time('getInventoryData');
    const res = await rest('/getInventoryData/' + warehouseId + '/' + headerId, 'GET');
    console.timeEnd('getInventoryData');
    return res;
};

export const getSupplierPriceListsWithItems = async (
    headerId: number | null,
    date?: number | null,
    companyId?: number | null
): Promise<SupplierPriceListsExtended[]> => {
    return headerId
        ? await rest('/getSupplierPriceListsWithItems/' + headerId, 'POST')
        : date
        ? await rest('/getSupplierPriceListsWithItems/null', 'POST', { date, companyId: companyId ? companyId : null })
        : await rest('/getSupplierPriceListsWithItems/null', 'POST', { companyId: companyId ? companyId : null });
};

export const checkSkuItemCodeToSupplierCode = async (
    companyId: number,
    itemList: SupplierPriceListToSheet[]
): Promise<SupplierPriceListToSheet[] | null> => {
    return await rest('/checkSkuItemCodeToSupplierCode/' + companyId, 'POST', {
        itemList: itemList,
    });
};

export const saveSupplierPriceList = async (
    header: SkuSupplierPriceListPerHeaderModel,
    itemList: SupplierPriceListToSheet[]
): Promise<SkuSupplierPriceListPerHeaderEditModel> => {
    return await rest('/saveSupplierPriceList', 'POST', { header: header, itemList: itemList });
};

export const createProcurementOrders = async (
    warehouseId: number,
    skuItemsCode: SkuItemSupplierCodeWithCompanyAndSkuItem[],
    nonExistingSkuItemsCode?: number[]
): Promise<void> => {
    return await rest('/createProcurementOrders/' + warehouseId, 'POST', {
        skuItemsCode: skuItemsCode,
        nonExistingSkuItemsCode: nonExistingSkuItemsCode ? nonExistingSkuItemsCode : [],
    });
};

export const getSkuItemSuppliers = async (
    skuItemIds: number[]
): Promise<SkuItemSupplierCodeWithCompanyAndSkuItem[]> => {
    return await rest('/getSkuItemSuppliers', 'POST', { skuItemIds: skuItemIds });
};

// ---ENTRY CALCULATION---

export const getEntryCalculations = async (
    getLockedEntryCalc?: boolean,
    page?: number,
    rows?: number
): Promise<PaginatedData> => {
    if (getLockedEntryCalc) {
        return await rest(
            '/getEntryCalculations?getLockedEntryCalc=' + getLockedEntryCalc + '&page=' + page + '&rows=' + rows,
            'GET'
        );
    } else {
        return await rest('/getEntryCalculations', 'GET');
    }
};

export const getEntryCalcWithItems = async (headerId: number): Promise<EntryCalcHeadersWithItems> => {
    return await rest('/getEntryCalcWithItems/' + headerId, 'GET');
};

export const saveEditedEntryCalc = async (
    entryCalc: EntryCalcHeadersWithItems,
    entryCalcItems: EntryCalculationItemModel[],
    editedColumn: string | null,
    selectedItemTypes?: number[]
): Promise<EntryCalcHeadersWithItems> => {
    return await rest('/saveEditedEntryCalc', 'POST', {
        entryCalc,
        entryCalcItems,
        editedColumn,
        selectedItemTypes: selectedItemTypes ? selectedItemTypes : [],
    });
};

export const createEntryCalc = async (entryCalc: EntryCalculationHeaderModel): Promise<EntryCalcHeadersWithItems> => {
    return await rest('/saveEditedEntryCalc', 'POST', {
        entryCalc,
        entryCalcItems: [],
    });
};

export const concludeEntryCalculation = async (headerId: number): Promise<void> => {
    return await rest('/concludeEntryCalculation/' + headerId, 'POST');
};

export const reactivateMPC = async (headerId: number): Promise<void> => {
    return await rest('/reactivateMPC/' + headerId, 'POST');
};

export const checkIfEntryCalcHeaderDocNumberExist = async (
    headerId: number | null,
    docNumber: string
): Promise<boolean> => {
    return await rest('/checkIfEntryCalcHeaderDocNumberExist/' + headerId + '/' + docNumber, 'GET');
};

export const concludeEntryCalcAndCreateDocument = async (headerId: number | null): Promise<number> => {
    return await rest('/concludeEntryCalcAndCreateDocument/' + headerId, 'POST');
};

export const getActiveStockReport = async (warehouseId: number | null): Promise<StockDocHeaderEditModel | null> => {
    const activeStockReport = await rest('/getActiveStockReport/' + warehouseId, 'GET');
    return activeStockReport;
};

export const savePosTerminals = async (posTerminals: PosTerminalEditModel[]): Promise<void> => {
    await rest('/savePosTerminals', 'POST', posTerminals);
};

export const updatePosTerminals = async (
    warehousePosTerminals: PosTerminalEditModel[] | null,
    posTerminals: PosTerminalEditModel[],
    warehouse: WarehouseModel
): Promise<void> => {
    const deleteWarehouseCandidates: PosTerminalEditModel[] = [];
    const newWarehouseCandidates: PosTerminalEditModel[] = [];

    posTerminals.forEach((p) => {
        const validPosTerminal = warehousePosTerminals?.find((pt) => pt.id === p.id && p.warehouseId === null);
        if (validPosTerminal) {
            newWarehouseCandidates.push(validPosTerminal);
        }
    });

    const allExistingWarehousePosTerminals = posTerminals.filter((p) => p.warehouseId === warehouse.id);
    for (const existingWarehousePosTerminal of allExistingWarehousePosTerminals) {
        const validPosTerminal = warehousePosTerminals?.find(
            (pt) => pt.id === existingWarehousePosTerminal.id && !warehouse?.isDeleted
        );
        if (!validPosTerminal) {
            deleteWarehouseCandidates.push(existingWarehousePosTerminal);
        }
    }

    const updatedPosTerminals: PosTerminalEditModel[] = [];
    for (const newWarehousePosTerminal of newWarehouseCandidates) {
        const copy = { ...newWarehousePosTerminal };
        copy.warehouseId = warehouse.id;
        updatedPosTerminals.push(copy);
    }

    for (const deleteWarehouseCandidate of deleteWarehouseCandidates) {
        const copy = { ...deleteWarehouseCandidate };
        copy.warehouseId = null;
        updatedPosTerminals.push(copy);
    }
    await savePosTerminals(updatedPosTerminals);
};

export const getLockedOrUnLockedPriceChangeLogsHeaders = async (
    type: PriceChangeLogHeaderTypeEnum,
    getLockedHeaders?: boolean
): Promise<PriceChangeLogsHeadersExtended[]> => {
    if (getLockedHeaders) {
        return await rest(
            '/getLockedOrUnLockedPriceChangeLogsHeaders?type=' + type + '&getLockedHeaders=' + getLockedHeaders,
            'GET'
        );
    } else {
        return await rest('/getLockedOrUnLockedPriceChangeLogsHeaders?type=' + type, 'GET');
    }
};

export const getPriceChangeLogHeaderWithItems = async (
    priceChangeLogId: number
): Promise<PriceChangeLogHeaderWithItems> => {
    return await rest('/getPriceChangeLogHeaderWithItems/' + priceChangeLogId, 'GET');
};

export const createPriceChangeLog = async (
    priceChangeLogHeader: PriceChangeLogHeaderModel
): Promise<PriceChangeLogHeaderWithItems> => {
    return await rest('/saveEditedPriceChangeLog', 'POST', {
        priceChangeLogHeader,
        priceChangeLogItems: [],
    });
};

export const saveEditedPriceChangeLog = async (
    priceChangeLogHeader: PriceChangeLogHeaderModel,
    priceChangeLogItems: PriceChangeLogItemModel[]
): Promise<PriceChangeLogHeaderWithItems> => {
    return await rest('/saveEditedPriceChangeLog', 'POST', {
        priceChangeLogHeader,
        priceChangeLogItems,
    });
};

export const copyPriceChangeLogDocument = async (
    priceChangeLogHeader: PriceChangeLogHeaderModel,
    priceChangeLogItems: PriceChangeLogItemModel[]
): Promise<PriceChangeLogHeaderWithItems> => {
    return await rest('/copyPriceChangeLogDocument', 'POST', {
        priceChangeLogHeader,
        priceChangeLogItems,
    });
};

export const closePriceChangeLog = async (priceChangeLogHeaderId: number): Promise<void> => {
    return await rest('/closePriceChangeLog/' + priceChangeLogHeaderId, 'POST');
};

export const checkIfPriceChangeLogDocNumberExist = async (
    priceChangeLogHeaderId: number | null,
    docNumber: string
): Promise<boolean> => {
    return await rest('/checkIfPriceChangeLogDocNumberExist/' + priceChangeLogHeaderId + '/' + docNumber, 'GET');
};

export const getPriceChangeLogNumSuggestion = async (): Promise<{ sequenceNumber: number; docNumber: string }> => {
    return await rest('/getPriceChangeLogNumSuggestion', 'GET');
};

export const getPrevOrNextPriceChangeLogHeader = async (type: string, selectedId: number): Promise<number> => {
    return await rest('/getPrevOrNextPriceChangeLogHeader?type=' + type + '&selectedId=' + selectedId, 'GET');
};

export const createOrEditInitialBalance = async (initialBalance: InitialBalanceModel): Promise<InitialBalanceModel> => {
    return await rest('/createOrEditInitialBalance', 'POST', { initialBalance });
};

export const concludeInitialBalance = async (initialBalanceId: number): Promise<void> => {
    return await rest('/concludeInitialBalance/' + initialBalanceId, 'POST');
};

export const getExcelFileForStockCard = async (
    skuItemId: number,
    from: number,
    to: number,
    otherSettings: any,
    warehouseId?: number | null
): Promise<void> => {
    const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : '';
    const res = await rest('/getExcelFileForStockCard/' + skuItemId + '?from=' + from + '&to=' + to + warehouseParam, 'GET');

    const base64 = res.file;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const dateFormatFromSettings = otherSettings?.dateFormat;

    const getDateFormat = (format: string | undefined): string => {
        if (!format) return 'DD_MM_YYYY';
        return format.replace(/[./-]/g, '_');
    };

    const dateFormat = getDateFormat(dateFormatFromSettings);

    const formatDate = (timestamp: number): string => {
        const d = new Date(timestamp);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear());

        return dateFormat.replace('DD', day).replace('MM', month).replace('YYYY', year);
    };

    const _to = translate('to');

    let datePart = '';
    if (from && to) {
        datePart = `_${formatDate(from)}_${_to}_${formatDate(to)}`;
    } else if (from) {
        datePart = `_${formatDate(from)}`;
    } else if (to) {
        datePart = `_${formatDate(to)}`;
    }
    let nameForxlsx = String(translate("stock_card"))

    a.download = `${nameForxlsx}${datePart}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const stornoEntryCalc = async (entryCalcHeaderId: number): Promise<EntryCalculationHeaderEditModel> => {
    return await rest('/stornoEntryCalc/' + entryCalcHeaderId, 'POST');
};
